/**
 * Canonical display-name resolution — the single source of truth for the
 * person's display name across the app.
 *
 * `profiles.first_name` / `profiles.last_name` is authoritative: it is written
 * at signup (the `handle_new_user` trigger), captured in onboarding, and is the
 * field an in-app profile edit persists. Historically the store's display name
 * was sourced from `auth.users.user_metadata` instead, so a name captured into
 * `profiles` (onboarding / edit) never surfaced — the metadata name (or email
 * prefix) won on every reload.
 *
 * Resolution order (never returns blank):
 *   1. `profiles.first_name` + `profiles.last_name` (the source of truth)
 *   2. `user_metadata.full_name` / `user_metadata.name` (OAuth accounts created
 *      before the name was captured into `profiles`)
 *   3. email local-part (profile email, else auth email)
 *   4. `'User'`
 */

type NameProfile = {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
};

type NameAuthUser = {
  email?: string | null;
  user_metadata?: Record<string, unknown> | null;
};

export function deriveDisplayName(
  profile: NameProfile | null | undefined,
  authUser?: NameAuthUser | null,
): string {
  const first = (profile?.first_name ?? '').trim();
  const last = (profile?.last_name ?? '').trim();
  const fromProfile = [first, last].filter(Boolean).join(' ').trim();
  if (fromProfile) return fromProfile;

  const meta = authUser?.user_metadata ?? {};
  const metaName = (meta.full_name ?? meta.name ?? '') as unknown;
  const fromMeta = typeof metaName === 'string' ? metaName.trim() : '';
  if (fromMeta) return fromMeta;

  const email = (profile?.email ?? authUser?.email ?? '').trim();
  const prefix = email.split('@')[0]?.trim();
  return prefix || 'User';
}
